<template>
  <div class="relative" ref="rootRef">
    <!-- Input trigger -->
    <button
      ref="buttonRef"
      type="button"
      @click="toggleOpen"
      :class="[
        'w-full min-w-0 max-w-full px-3 sm:px-4 py-3 bg-bochas-bg border rounded-lg text-white text-left flex items-center justify-between gap-2 transition-colors focus:outline-none',
        open ? 'border-bochas-rojo' : 'border-gray-700 hover:border-gray-500',
      ]"
    >
      <span :class="displayValue ? 'text-white' : 'text-gray-500'">
        {{ displayValue || placeholder }}
      </span>
      <IconFbochas name="calendarDays" :size="18" class="text-gray-300 flex-shrink-0" />
    </button>

    <!-- Hidden native input para que formularios y screen readers lo entiendan -->
    <input
      type="hidden"
      :name="name"
      :value="modelValue"
    />

    <!-- Dropdown del calendario -->
    <Teleport v-if="fixedDropdown" to="body">
      <Transition name="datepicker-fade">
        <div
          v-if="open"
          :style="fixedDropdownStyle"
          class="datepicker-fixed-menu absolute z-[9999] bg-[#1a2030] border border-white/10 rounded-xl shadow-2xl p-3 sm:p-4"
        >
          <!-- Header mes -->
          <div class="flex items-center justify-between mb-3">
            <button
              type="button"
              @click="prevMonth"
              class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 text-white"
              aria-label="Mes anterior"
            >
              <IconFbochas name="chevron-left" :size="18" />
            </button>
            <div class="flex min-w-0 items-center justify-center gap-2">
              <button
                type="button"
                class="flex min-w-[112px] items-center justify-between gap-2 rounded-lg border border-white/10 bg-[#121827] px-3 py-2 text-sm font-semibold capitalize text-white transition-colors hover:border-white/25"
                @click="toggleHeaderPicker('month')"
              >
                <span>{{ MONTHS_ES[view.m] }}</span>
                <IconFbochas name="chevron-down" :size="14" />
              </button>
              <button
                type="button"
                class="flex min-w-[82px] items-center justify-between gap-2 rounded-lg border border-white/10 bg-[#121827] px-3 py-2 text-sm font-semibold text-white transition-colors hover:border-white/25"
                @click="toggleHeaderPicker('year')"
              >
                <span>{{ view.y }}</span>
                <IconFbochas name="chevron-down" :size="14" />
              </button>
            </div>
            <button
              type="button"
              @click="nextMonth"
              class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 text-white"
              aria-label="Mes siguiente"
            >
              <IconFbochas name="chevron-right" :size="18" />
            </button>
          </div>

          <div
            v-if="headerPicker"
            class="absolute inset-x-3 top-[60px] z-20 rounded-xl border border-white/10 bg-[#161d2d] p-3 shadow-2xl sm:inset-x-4"
          >
            <div v-if="headerPicker === 'month'" class="grid grid-cols-3 gap-2">
              <button
                v-for="(month, index) in MONTHS_ES"
                :key="month"
                type="button"
                :class="[
                  'rounded-lg px-2 py-2.5 text-sm capitalize transition-colors',
                  view.m === index
                    ? 'bg-bochas-rojo font-semibold text-white'
                    : 'text-gray-200 hover:bg-white/10',
                ]"
                @click="setMonth(index)"
              >
                {{ month.slice(0, 3) }}
              </button>
            </div>
            <template v-else>
              <div class="mb-3 flex items-center justify-between">
                <button
                  type="button"
                  class="flex h-8 w-8 items-center justify-center rounded-full text-white hover:bg-white/10 disabled:opacity-30"
                  :disabled="yearPageStart <= minSelectableYear"
                  @click="changeYearPage(-12)"
                >
                  <IconFbochas name="chevron-left" :size="16" />
                </button>
                <span class="text-sm font-semibold text-white">{{ yearPageLabel }}</span>
                <button
                  type="button"
                  class="flex h-8 w-8 items-center justify-center rounded-full text-white hover:bg-white/10 disabled:opacity-30"
                  :disabled="yearPageStart + 11 >= maxSelectableYear"
                  @click="changeYearPage(12)"
                >
                  <IconFbochas name="chevron-right" :size="16" />
                </button>
              </div>
              <div class="grid grid-cols-4 gap-2">
                <button
                  v-for="year in visibleYears"
                  :key="year"
                  type="button"
                  :class="[
                    'rounded-lg px-2 py-2.5 text-sm transition-colors',
                    view.y === year
                      ? 'bg-bochas-rojo font-semibold text-white'
                      : 'text-gray-200 hover:bg-white/10',
                  ]"
                  @click="setYear(year)"
                >
                  {{ year }}
                </button>
              </div>
            </template>
          </div>

          <!-- Días de la semana -->
          <div class="grid grid-cols-7 gap-1 mb-1">
            <span
              v-for="d in weekDays"
              :key="d"
              class="text-center text-[10px] font-semibold uppercase text-gray-400 py-1"
            >
              {{ d }}
            </span>
          </div>

          <!-- Grid de días -->
          <div class="grid grid-cols-7 gap-1">
            <button
              v-for="cell in cells"
              :key="cell.key"
              type="button"
              :disabled="!cell.inMonth"
              @click="selectDay(cell)"
              :class="[
                'h-9 sm:h-10 rounded-md text-sm transition-colors',
                !cell.inMonth
                  ? 'text-gray-600 cursor-default'
                  : cell.selected
                  ? 'bg-bochas-rojo text-white font-semibold'
                  : cell.isToday
                  ? 'border border-bochas-rojo text-white hover:bg-white/10'
                  : 'text-gray-200 hover:bg-white/10',
              ]"
            >
              {{ cell.day }}
            </button>
          </div>

          <!-- Acciones -->
          <div class="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
            <button
              type="button"
              @click="clearValue"
              class="text-xs text-gray-400 hover:text-white"
            >
              Limpiar
            </button>
            <button
              type="button"
              @click="setToday"
              class="text-xs text-bochas-rojo hover:underline"
            >
              Hoy
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>

    <Transition v-else name="datepicker-fade">
      <div
        v-if="open"
        :class="[
          'absolute z-50 left-0 right-0 sm:right-auto sm:w-80 bg-[#1a2030] border border-white/10 rounded-xl shadow-2xl p-3 sm:p-4',
          dropdownPlacement === 'top' ? 'bottom-full mb-2' : 'mt-2',
        ]"
      >
        <!-- Header mes -->
        <div class="flex items-center justify-between mb-3">
          <button
            type="button"
            @click="prevMonth"
            class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 text-white"
            aria-label="Mes anterior"
          >
            <IconFbochas name="chevron-left" :size="18" />
          </button>
          <div class="flex min-w-0 items-center justify-center gap-2">
            <button
              type="button"
              class="flex min-w-[112px] items-center justify-between gap-2 rounded-lg border border-white/10 bg-[#121827] px-3 py-2 text-sm font-semibold capitalize text-white transition-colors hover:border-white/25"
              @click="toggleHeaderPicker('month')"
            >
              <span>{{ MONTHS_ES[view.m] }}</span>
              <IconFbochas name="chevron-down" :size="14" />
            </button>
            <button
              type="button"
              class="flex min-w-[82px] items-center justify-between gap-2 rounded-lg border border-white/10 bg-[#121827] px-3 py-2 text-sm font-semibold text-white transition-colors hover:border-white/25"
              @click="toggleHeaderPicker('year')"
            >
              <span>{{ view.y }}</span>
              <IconFbochas name="chevron-down" :size="14" />
            </button>
          </div>
          <button
            type="button"
            @click="nextMonth"
            class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 text-white"
            aria-label="Mes siguiente"
          >
            <IconFbochas name="chevron-right" :size="18" />
          </button>
        </div>

        <div
          v-if="headerPicker"
          class="absolute inset-x-3 top-[60px] z-20 rounded-xl border border-white/10 bg-[#161d2d] p-3 shadow-2xl sm:inset-x-4"
        >
          <div v-if="headerPicker === 'month'" class="grid grid-cols-3 gap-2">
            <button
              v-for="(month, index) in MONTHS_ES"
              :key="month"
              type="button"
              :class="[
                'rounded-lg px-2 py-2.5 text-sm capitalize transition-colors',
                view.m === index
                  ? 'bg-bochas-rojo font-semibold text-white'
                  : 'text-gray-200 hover:bg-white/10',
              ]"
              @click="setMonth(index)"
            >
              {{ month.slice(0, 3) }}
            </button>
          </div>
          <template v-else>
            <div class="mb-3 flex items-center justify-between">
              <button
                type="button"
                class="flex h-8 w-8 items-center justify-center rounded-full text-white hover:bg-white/10 disabled:opacity-30"
                :disabled="yearPageStart <= minSelectableYear"
                @click="changeYearPage(-12)"
              >
                <IconFbochas name="chevron-left" :size="16" />
              </button>
              <span class="text-sm font-semibold text-white">{{ yearPageLabel }}</span>
              <button
                type="button"
                class="flex h-8 w-8 items-center justify-center rounded-full text-white hover:bg-white/10 disabled:opacity-30"
                :disabled="yearPageStart + 11 >= maxSelectableYear"
                @click="changeYearPage(12)"
              >
                <IconFbochas name="chevron-right" :size="16" />
              </button>
            </div>
            <div class="grid grid-cols-4 gap-2">
              <button
                v-for="year in visibleYears"
                :key="year"
                type="button"
                :class="[
                  'rounded-lg px-2 py-2.5 text-sm transition-colors',
                  view.y === year
                    ? 'bg-bochas-rojo font-semibold text-white'
                    : 'text-gray-200 hover:bg-white/10',
                ]"
                @click="setYear(year)"
              >
                {{ year }}
              </button>
            </div>
          </template>
        </div>

        <!-- Días de la semana -->
        <div class="grid grid-cols-7 gap-1 mb-1">
          <span
            v-for="d in weekDays"
            :key="d"
            class="text-center text-[10px] font-semibold uppercase text-gray-400 py-1"
          >
            {{ d }}
          </span>
        </div>

        <!-- Grid de días -->
        <div class="grid grid-cols-7 gap-1">
          <button
            v-for="cell in cells"
            :key="cell.key"
            type="button"
            :disabled="!cell.inMonth"
            @click="selectDay(cell)"
            :class="[
              'h-9 sm:h-10 rounded-md text-sm transition-colors',
              !cell.inMonth
                ? 'text-gray-600 cursor-default'
                : cell.selected
                ? 'bg-bochas-rojo text-white font-semibold'
                : cell.isToday
                ? 'border border-bochas-rojo text-white hover:bg-white/10'
                : 'text-gray-200 hover:bg-white/10',
            ]"
          >
            {{ cell.day }}
          </button>
        </div>

        <!-- Acciones -->
        <div class="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
          <button
            type="button"
            @click="clearValue"
            class="text-xs text-gray-400 hover:text-white"
          >
            Limpiar
          </button>
          <button
            type="button"
            @click="setToday"
            class="text-xs text-bochas-rojo hover:underline"
          >
            Hoy
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import IconFbochas from "@/components/Icons/IconFbochas.vue";

const props = defineProps({
  modelValue: { type: String, default: "" }, // "YYYY-MM-DD"
  placeholder: { type: String, default: "Seleccionar fecha" },
  name: { type: String, default: "" },
  dropdownPlacement: { type: String, default: "bottom" },
  fixedDropdown: { type: Boolean, default: false },
  minYear: { type: Number, default: 1900 },
  maxYear: { type: Number, default: null },
});

const emit = defineEmits(["update:modelValue"]);

const open = ref(false);
const rootRef = ref(null);
const buttonRef = ref(null);
const fixedDropdownStyle = ref({});
const view = ref({ y: 1970, m: 0 }); // mes actualmente visible
const headerPicker = ref(null);
const yearPageStart = ref(0);

const MONTHS_ES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];
const weekDays = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

const minSelectableYear = computed(() => Math.min(props.minYear, view.value.y));
const maxSelectableYear = computed(() =>
  Math.max(props.maxYear ?? new Date().getFullYear() + 20, view.value.y),
);
const visibleYears = computed(() =>
  Array.from({ length: 12 }, (_, index) => yearPageStart.value + index).filter(
    (year) =>
      year >= minSelectableYear.value && year <= maxSelectableYear.value,
  ),
);
const yearPageLabel = computed(() => {
  const years = visibleYears.value;
  return years.length ? `${years[0]} – ${years[years.length - 1]}` : "";
});

const today = new Date();
const todayKey = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;

const selectedKey = computed(() => {
  if (!props.modelValue) return null;
  const [y, m, d] = props.modelValue.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return `${y}-${m - 1}-${d}`;
});

const displayValue = computed(() => {
  if (!props.modelValue) return "";
  const [y, m, d] = props.modelValue.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return "";
  return `${d} de ${MONTHS_ES[m - 1]} de ${y}`;
});

const cells = computed(() => {
  const y = view.value.y;
  const m = view.value.m;
  const firstOfMonth = new Date(y, m, 1);
  const firstDow = firstOfMonth.getDay(); // 0=domingo
  // Empezamos el grid en el domingo anterior o igual.
  const start = new Date(y, m, 1 - firstDow);
  const arr = [];
  for (let i = 0; i < 42; i++) {
    const dt = new Date(start);
    dt.setDate(start.getDate() + i);
    const inMonth = dt.getMonth() === m;
    const key = `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`;
    const isToday = key === todayKey;
    const sel = key === selectedKey.value;
    arr.push({
      key,
      day: dt.getDate(),
      inMonth,
      isToday,
      selected: sel,
      y: dt.getFullYear(),
      m: dt.getMonth(),
      d: dt.getDate(),
    });
  }
  return arr;
});

function prevMonth() {
  headerPicker.value = null;
  if (view.value.m === 0) {
    view.value = { y: view.value.y - 1, m: 11 };
  } else {
    view.value = { y: view.value.y, m: view.value.m - 1 };
  }
}
function nextMonth() {
  headerPicker.value = null;
  if (view.value.m === 11) {
    view.value = { y: view.value.y + 1, m: 0 };
  } else {
    view.value = { y: view.value.y, m: view.value.m + 1 };
  }
}

function setMonth(value) {
  view.value = { y: view.value.y, m: Number(value) };
  headerPicker.value = null;
}

function setYear(value) {
  view.value = { y: Number(value), m: view.value.m };
  headerPicker.value = null;
}

function toggleHeaderPicker(type) {
  headerPicker.value = headerPicker.value === type ? null : type;
  if (headerPicker.value === "year") {
    yearPageStart.value = Math.floor(view.value.y / 12) * 12;
  }
}

function changeYearPage(step) {
  const next = yearPageStart.value + step;
  yearPageStart.value = Math.max(
    Math.floor(minSelectableYear.value / 12) * 12,
    Math.min(next, Math.floor(maxSelectableYear.value / 12) * 12),
  );
}

function pad(n) {
  return String(n).padStart(2, "0");
}

function selectDay(cell) {
  if (!cell.inMonth) return;
  const val = `${cell.y}-${pad(cell.m + 1)}-${pad(cell.d)}`;
  emit("update:modelValue", val);
  open.value = false;
}

function updateFixedDropdownPosition() {
  if (!props.fixedDropdown || !buttonRef.value) return;
  const rect = buttonRef.value.getBoundingClientRect();
  const width = Math.min(320, window.innerWidth - 24);
  const left = Math.min(Math.max(12, rect.left), window.innerWidth - width - 12);
  const height = 374;
  const spaceBelow = window.innerHeight - rect.bottom;
  const top =
    spaceBelow >= height + 8
      ? rect.bottom + window.scrollY + 8
      : Math.max(window.scrollY + 12, rect.top + window.scrollY - height - 8);
  fixedDropdownStyle.value = {
    top: `${top}px`,
    left: `${left}px`,
    width: `${width}px`,
  };
}

function toggleOpen() {
  open.value = !open.value;
  headerPicker.value = null;
  if (open.value) updateFixedDropdownPosition();
}

function clearValue() {
  emit("update:modelValue", "");
  open.value = false;
}

function setToday() {
  const val = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  emit("update:modelValue", val);
  view.value = { y: today.getFullYear(), m: today.getMonth() };
  open.value = false;
}

function onDocClick(e) {
  if (!rootRef.value) return;
  if (
    !rootRef.value.contains(e.target) &&
    !e.target.closest(".datepicker-fixed-menu")
  ) {
    open.value = false;
  }
}

onMounted(() => {
  document.addEventListener("mousedown", onDocClick);
  window.addEventListener("resize", updateFixedDropdownPosition);
  // Inicializar la vista en la fecha seleccionada o en hoy.
  if (props.modelValue) {
    const [y, m] = props.modelValue.split("-").map((n) => parseInt(n, 10));
    if (y && m) view.value = { y, m: m - 1 };
  } else {
    view.value = { y: today.getFullYear(), m: today.getMonth() };
  }
});
onBeforeUnmount(() => {
  document.removeEventListener("mousedown", onDocClick);
  window.removeEventListener("resize", updateFixedDropdownPosition);
});

watch(
  () => props.modelValue,
  (val) => {
    if (!val) return;
    const [y, m] = val.split("-").map((n) => parseInt(n, 10));
    if (y && m) view.value = { y, m: m - 1 };
  },
);
</script>

<style scoped>
.datepicker-fade-enter-active,
.datepicker-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.datepicker-fade-enter-from,
.datepicker-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
